import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Home, Palette, Store, Image, Ticket } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import { useAdminStore } from '@/lib/admin-store';
import { useMenuLabel } from '@/lib/menu-labels';

const BottomNav: React.FC = () => {
  const location = useLocation();
  const { t } = useTranslation();
  const menuVis = useAdminStore((s) => s.settings?.menuVisibility) ?? { home: true, shop: true, artists: true, exhibitions: true, artFairs: true, dropshop: true, news: true, contact: true, team: true, faq: true };
  const menuLabel = useMenuLabel();

  const isActive = (href: string): boolean => {
    if (href === '/') return location.pathname === '/';
    return location.pathname.startsWith(href);
  };

  const tabs = [
    menuVis.home !== false && { label: menuLabel('home', t.nav.home), href: '/', icon: Home },
    menuVis.artists !== false && { label: menuLabel('artists', t.nav.artists), href: '/artists', icon: Palette },
    menuVis.shop !== false && { label: menuLabel('shop', t.nav.shop), href: '/shop', icon: Store, center: true },
    menuVis.exhibitions !== false && { label: menuLabel('exhibitions', t.nav.exhibitions), href: '/exhibitions', icon: Image },
    menuVis.artFairs !== false && { label: menuLabel('artFairs', t.nav.artFairs), href: '/art-fairs', icon: Ticket },
  ].filter(Boolean) as Array<{ label: string; href: string; icon: React.ElementType; center?: boolean }>;

  return (
    <nav className="fixed bottom-0 left-0 right-0 z-[500] lg:hidden">
      {/* Glassmorphism background */}
      <div className="absolute inset-0 bg-white/80 dark:bg-black/80 backdrop-blur-xl border-t border-black/[0.06] dark:border-white/[0.06]" />

      {/* Safe area + tabs */}
      <div className="relative flex items-end justify-around px-2 pt-1.5 pb-[max(0.5rem,env(safe-area-inset-bottom))]">
        {tabs.map((tab) => {
          const active = isActive(tab.href);
          const Icon = tab.icon;

          if (tab.center) {
            // Elevated center Shop button
            return (
              <Link
                key={tab.href}
                to={tab.href}
                className="relative flex flex-col items-center -mt-4"
              >
                <motion.div
                  whileTap={{ scale: 0.9 }}
                  className={`w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg transition-all duration-300 ${
                    active
                      ? 'bg-black dark:bg-white text-white dark:text-black shadow-black/20 dark:shadow-white/20'
                      : 'bg-black dark:bg-white text-white dark:text-black shadow-black/10 dark:shadow-white/10'
                  }`}
                >
                  <Icon size={20} strokeWidth={2.5} />
                </motion.div>
                <span
                  className={`text-[8px] font-bold uppercase tracking-wider mt-1 transition-colors duration-300 ${
                    active ? 'text-black dark:text-white' : 'text-black/40 dark:text-white/40'
                  }`}
                >
                  {tab.label}
                </span>
              </Link>
            );
          }

          // Regular tab
          return (
            <Link
              key={tab.href}
              to={tab.href}
              className="flex flex-col items-center min-w-[3.5rem] py-1"
            >
              <motion.div whileTap={{ scale: 0.9 }} className="relative">
                <Icon
                  size={22}
                  strokeWidth={active ? 2.5 : 1.5}
                  className={`transition-all duration-300 ${
                    active ? 'text-black dark:text-white' : 'text-black/30 dark:text-white/30'
                  }`}
                />
                {active && (
                  <motion.div
                    layoutId="bottom-nav-dot"
                    className="absolute -top-1.5 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-black dark:bg-white"
                    transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                  />
                )}
              </motion.div>
              <span
                className={`text-[8px] font-bold uppercase tracking-wider mt-1 transition-colors duration-300 ${
                  active ? 'text-black dark:text-white' : 'text-black/30 dark:text-white/30'
                }`}
              >
                {tab.label}
              </span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
};

export default BottomNav;
